export interface TubeSeoProps {
  partNumber?: string;        // e.g., "12AX7", "6L6GC"
  brand?: string;             // e.g., "Mullard", "RCA", "Telefunken"
  condition?: string;         // e.g., "NOS", "Slightly Used", "Tested"
  listingTitle?: string;      // Fallback custom title (has "| TubeDealer" appended)
  /**
   * A fully-formed <title> string that bypasses the title-builder entirely.
   * Use this when the caller already has the complete title (e.g. entries in
   * shared/staticPageMeta.ts) so the site-name suffix is never appended twice.
   * Keep in sync with the server copy in artifacts/api-server/src/shared/seo.ts.
   */
  rawTitle?: string;
  description?: string;       // Item description or custom excerpt
  imageUrl?: string;          // Primary box or tube image
  canonicalUrl?: string;      // Page canonical URL
  price?: number;             // Optional item price
  currency?: string;          // Default: "USD"
  siteName?: string;          // Default: "Tube Dealer"
  /** Override og:title independently of the <title> tag (e.g. share-optimised copy) */
  ogTitle?: string;
  /** Override og:description independently of <meta name="description"> */
  ogDescription?: string;
  /** Render <meta name="robots" content="noindex, nofollow"> for private pages */
  noindex?: boolean;
}

export interface GeneratedSeoData {
  title: string;
  description: string;
  canonicalUrl: string;
  ogImage: string;
  siteName: string;
  /** og:title — falls back to title when not explicitly set */
  ogTitle: string;
  /** og:description — falls back to description when not explicitly set */
  ogDescription: string;
}

const DEFAULT_SITE_NAME = "TubeDealer";
const DEFAULT_DOMAIN = "https://tubedealer.com";
// opengraph.jpg is a 1200×630 OG card served from the tubedealer public dir.
// Must be an absolute HTTPS URL so social crawlers (WhatsApp, Slack, iMessage)
// can fetch it without knowing the deployment domain.
const DEFAULT_IMAGE = `${DEFAULT_DOMAIN}/opengraph.jpg`;
const DEFAULT_DESCRIPTION =
  "TubeDealer is the marketplace for modern production and vintage NOS vacuum tubes. Import supplier CSVs, scan inventory with AI, and sell across channels.";

/** Build the canonical public URL while preserving the requested pathname. */
export function buildCanonicalUrl(pathOrUrl?: string): string {
  const requested =
    pathOrUrl ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const parsed = new URL(requested, DEFAULT_DOMAIN);
  return `${DEFAULT_DOMAIN}${parsed.pathname || "/"}${parsed.search}`;
}

/**
 * Builds dynamic titles, meta descriptions, and fallback defaults for tube listings and site pages.
 */
export function generateSeoMetadata(props: TubeSeoProps = {}): GeneratedSeoData {
  const siteName = props.siteName || DEFAULT_SITE_NAME;
  const canonicalUrl = buildCanonicalUrl(props.canonicalUrl);
  const ogImage = props.imageUrl || DEFAULT_IMAGE;

  // 1. Build Dynamic Page Title
  // rawTitle short-circuits the builder — use it when the caller already has
  // a complete title string to avoid appending "| TubeDealer" a second time.
  let title = "";
  if (props.rawTitle) {
    title = props.rawTitle;
  } else if (props.partNumber && props.brand) {
    const conditionTag = props.condition ? ` ${props.condition}` : "";
    title = `${props.partNumber} ${props.brand}${conditionTag} Vacuum Tube | ${siteName}`;
  } else if (props.partNumber) {
    const conditionTag = props.condition ? ` ${props.condition}` : "";
    title = `${props.partNumber}${conditionTag} Vacuum Tube | ${siteName}`;
  } else if (props.listingTitle) {
    title = `${props.listingTitle} | ${siteName}`;
  } else {
    title = `${siteName} | Modern & Vintage Vacuum Tube Marketplace`;
  }

  // 2. Build Dynamic Meta Description
  let description = "";
  if (props.description && props.description.trim().length > 0) {
    // Truncate long descriptions cleanly at ~155 characters
    description = props.description.length > 155
      ? `${props.description.slice(0, 152).trim()}...`
      : props.description;
  } else if (props.partNumber) {
    const brandStr = props.brand ? `${props.brand} ` : "";
    const condStr = props.condition ? `${props.condition} ` : "";
    description = `Buy tested ${brandStr}${props.partNumber} ${condStr}vacuum tubes. Guaranteed test readings, original box inspection, and secure shipping on ${siteName}.`;
  } else {
    description = DEFAULT_DESCRIPTION;
  }

  return {
    title,
    description,
    canonicalUrl,
    ogImage,
    siteName,
    ogTitle:       props.ogTitle       ?? title,
    ogDescription: props.ogDescription ?? description,
  };
}
